import { zodResolver } from "@hookform/resolvers/zod";
import { LoadingButton } from "@mui/lab";
import { Autocomplete, Box, Button, DialogActions, DialogContent, InputAdornment, TextField } from "@mui/material";
import { AnimatePresence } from "motion/react";
import { enqueueSnackbar } from "notistack";
import { useEffect } from "react";
import {
    Control,
    Controller,
    FieldErrors,
    FieldValues,
    FormProvider,
    Path,
    UseFormRegister,
    useForm,
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import { TbIdBadge2, TbLock, TbLockPin, TbMail, TbUser, TbUserHexagon, TbUserScan } from "react-icons/tb";
import { z } from "zod";

import AirportField from "@/components/AirportField";
import { CommonHelperText, CommonInput, CommonLabel } from "@/components/CommonInput";
import { Airpots } from "@/constants/airpots";
import { useCreateUserMutation } from "@/requests/user.mutations";
import { useUsersTableActions } from "@/stores/use-users-table";
import { UserType } from "@/types/user.types";

import { RenderUserTypeCell } from "../UserTypeColumn";

const formSchema = z
    .object({
        identityNo: z.string().nullable(),
        userName: z.string().min(3).max(200),
        name: z.string().min(2).max(200),
        surname: z.string().min(2).max(200),
        email: z.string().email(),
        password: z.string().min(5).max(100),
        passwordConfirm: z.string().min(5).max(100),
        userType: z.nativeEnum(UserType),
        airport: z.nativeEnum(Airpots).nullable(),
    })
    .superRefine(({ userType, password, passwordConfirm, identityNo, airport }, ctx) => {
        if (password !== passwordConfirm) {
            ctx.addIssue({
                path: ["passwordConfirm"],
                message: "Şifreler eşleşmiyor.",
                code: z.ZodIssueCode.custom,
            });
        }

        if (userType === UserType.DRIVER) {
            if (!identityNo)
                ctx.addIssue({
                    path: ["identityNo"],
                    message: "TC kimlik numarası zorunludur.",
                    code: z.ZodIssueCode.custom,
                });
            else if (identityNo?.length !== 11)
                ctx.addIssue({
                    path: ["identityNo"],
                    message: "TC kimlik numarası 11 haneli olmalıdır.",
                    code: z.ZodIssueCode.custom,
                });
        } else if (userType === UserType.WELCOME_TEAM_OFFICER && !airport) {
            ctx.addIssue({
                path: ["airport"],
                message: "Karşılama ekibi için havalimanı zorunludur.",
                code: z.ZodIssueCode.custom,
            });
        }
    });

export type CreateUserFormType = z.infer<typeof formSchema>;
function UserCreateDialog() {
    const { t } = useTranslation();
    const { isPending: loading, mutateAsync } = useCreateUserMutation();
    const methods = useForm({
        defaultValues: {
            identityNo: "",
            userName: "",
            name: "",
            surname: "",
            email: "",
            password: "",
            passwordConfirm: "",
            userType: UserType.DRIVER,
            airport: null,
        },
        disabled: loading,
        resolver: zodResolver(formSchema),
    });
    const {
        handleSubmit,
        register,
        formState: { errors },
        control,
        watch,
        setValue,
    } = methods;
    const userType = watch("userType") as UserType;
    const airport = watch("airport") as Airpots | null;
    const { updateShowCreateDialog } = useUsersTableActions();
    const createUser = async (data: CreateUserFormType) => {
        if (loading) return;
        try {
            const res = await mutateAsync(data);
            const message = res.message || "";
            if (res.ok) {
                handleClose();
                enqueueSnackbar(message, {
                    variant: "success",
                });
            } else {
                enqueueSnackbar(message, {
                    variant: "error",
                });
            }
        } catch (error) {
            console.error("User error:", error);
        }
    };

    const handleClose = () => {
        updateShowCreateDialog(false);
    };
    useEffect(() => {
        if (userType !== UserType.DRIVER) {
            setValue("identityNo", "");
        }
        if (userType !== UserType.WELCOME_TEAM_OFFICER) {
            setValue("airport", null);
        }
        setValue("password", "");
        setValue("passwordConfirm", "");
    }, [userType]);

    return (
        <FormProvider {...methods}>
            <DialogContent dividers>
                <form autoComplete="off" onSubmit={handleSubmit(createUser)} className="grid grid-cols-8 gap-7">
                    <UserTypeField
                        userType={userType}
                        control={control as Control<CreateUserFormType>}
                        errors={errors}
                        loading={loading}
                    />
                    <CommonLabel required label={t("userName")} className=" col-span-full">
                        <CommonInput
                            error={!!errors?.userName}
                            {...register("userName")}
                            placeholder={t("placeholders.enter", { field: t("userName") })}
                            startAdornment={
                                <InputAdornment position="start">
                                    <TbUserHexagon />
                                </InputAdornment>
                            }
                        />
                        <CommonHelperText text={errors?.userName?.message || ""} />
                    </CommonLabel>

                    <CommonLabel required label={t("name")} className=" col-span-4">
                        <CommonInput
                            error={!!errors?.name}
                            {...register("name")}
                            placeholder={t("placeholders.enter", { field: t("name") })}
                            startAdornment={
                                <InputAdornment position="start">
                                    <TbUser />
                                </InputAdornment>
                            }
                        />
                        <CommonHelperText text={errors?.name?.message || ""} />
                    </CommonLabel>
                    <CommonLabel required label={t("surname")} className=" col-span-4">
                        <CommonInput
                            error={!!errors?.surname}
                            {...register("surname")}
                            placeholder={t("placeholders.enter", { field: t("surname") })}
                            startAdornment={
                                <InputAdornment position="start">
                                    <TbUser />
                                </InputAdornment>
                            }
                        />
                        <CommonHelperText text={errors?.surname?.message || ""} />
                    </CommonLabel>
                    <CommonLabel required label={t("password")} className=" col-span-4">
                        <CommonInput
                            error={!!errors?.password}
                            {...register("password")}
                            type="password"
                            placeholder={t("placeholders.enter", { field: t("password") })}
                            startAdornment={
                                <InputAdornment position="start">
                                    <TbLock />
                                </InputAdornment>
                            }
                        />
                        <CommonHelperText text={errors?.password?.message || ""} />
                    </CommonLabel>
                    <CommonLabel required label={t("passwordConfirm")} className=" col-span-4">
                        <CommonInput
                            error={!!errors?.passwordConfirm}
                            type="password"
                            {...register("passwordConfirm")}
                            placeholder={t("placeholders.enter", { field: t("passwordConfirm") })}
                            startAdornment={
                                <InputAdornment position="start">
                                    <TbLockPin />
                                </InputAdornment>
                            }
                        />
                        <CommonHelperText text={errors?.passwordConfirm?.message || ""} />
                    </CommonLabel>
                    <CommonLabel required label={t("email")} className=" col-span-4">
                        <CommonInput
                            error={!!errors?.email}
                            {...register("email")}
                            placeholder={t("placeholders.enter", { field: t("email") })}
                            startAdornment={
                                <InputAdornment position="start">
                                    <TbMail />
                                </InputAdornment>
                            }
                        />
                        <CommonHelperText text={errors?.email?.message || ""} />
                    </CommonLabel>
                    <AnimatePresence mode="sync">
                        {userType === UserType.DRIVER ? (
                            <IdentityNoField
                                errors={errors}
                                loading={loading}
                                register={register}
                                className="col-span-4"
                            />
                        ) : userType === UserType.WELCOME_TEAM_OFFICER ? (
                            <AirportField<CreateUserFormType>
                                className="col-span-4"
                                formPath="airport"
                                label="authorizedAirport"
                                airport={airport}
                                loading={loading}
                            />
                        ) : null}
                    </AnimatePresence>
                </form>
            </DialogContent>
            <DialogActions>
                <Button disabled={loading} autoFocus color="secondary" variant="outlined" onClick={handleClose}>
                    {t("buttons.cancel")}
                </Button>

                <LoadingButton
                    loading={loading}
                    type="submit"
                    variant="contained"
                    color="primary"
                    onClick={handleSubmit(createUser)}
                >
                    {t("buttons.create")}
                </LoadingButton>
            </DialogActions>
        </FormProvider>
    );
}

export const IdentityNoField = <T extends { identityNo: string }>({
    errors,
    register,
    loading,
    className,
}: {
    errors: FieldErrors<T>;
    loading: boolean;
    register: UseFormRegister<T>;
    className: string;
}) => {
    const { t } = useTranslation();
    return (
        <CommonLabel required label={t("citizen_id")} className={className}>
            <CommonInput
                error={!!errors?.identityNo}
                disabled={loading}
                {...register("identityNo" as Path<T>)}
                placeholder={t("placeholders.enter", { field: t("citizen_id") })}
                startAdornment={
                    <InputAdornment position="start">
                        <TbIdBadge2 />
                    </InputAdornment>
                }
            />
            <CommonHelperText text={String(errors?.identityNo?.message || "")} />
        </CommonLabel>
    );
};

const userTypeOptions = [UserType.DRIVER, UserType.WELCOME_TEAM_OFFICER, UserType.ADMIN, UserType.MANAGER, UserType.HR];
export const UserTypeField = <T extends FieldValues>({
    control,
    errors,
    userType,
    loading,
}: {
    control: Control<T>;
    errors: FieldErrors<{ userType: number | undefined }>;
    userType: UserType;
    loading: boolean;
}) => {
    const { t } = useTranslation();
    return (
        <CommonLabel label={t("userType")} className=" col-span-4">
            <Controller
                name={"userType" as Path<T>}
                control={control}
                render={({ field }) => (
                    <Autocomplete
                        {...field}
                        classes={{
                            inputRoot: " !h-[47px]",
                        }}
                        sx={() => ({
                            "label + &": {
                                marginTop: 20,
                            },
                            "& .MuiInputBase-input": {
                                paddingBottom: 12,
                                paddingTop: 12,
                            },
                        })}
                        value={userType}
                        options={userTypeOptions}
                        disabled={loading}
                        onChange={(_, value) => field.onChange(value)}
                        getOptionLabel={(val) => {
                            return t(val ? "userTypeLabels." + val : "");
                        }}
                        renderOption={(props, val) => {
                            const { key, ...optionProps } = props;

                            return (
                                <Box
                                    key={key}
                                    component="li"
                                    sx={{
                                        display: "flex",
                                        justifyContent: "center",
                                    }}
                                    {...optionProps}
                                >
                                    <RenderUserTypeCell value={val} />
                                </Box>
                            );
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                placeholder={t("placeholders.select", { field: t("userType") })}
                                error={!!errors?.userType}
                                helperText={errors?.userType?.message}
                                variant="filled"
                                sx={{
                                    "& .MuiInputBase-input": {
                                        transform: "translateY(-8px)",
                                    },
                                }}
                                InputProps={{
                                    ...params.InputProps,
                                    startAdornment: (
                                        <InputAdornment position="end" className="  !-translate-x-1 !-translate-y-2">
                                            <TbUserScan />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        )}
                    />
                )}
            />
        </CommonLabel>
    );
};
export default UserCreateDialog;
